import { useQuery } from "@tanstack/react-query";
import { ShipTier } from "@voidex/contracts-ts";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { FarmOutcome, type ResolvedMission } from "../../hooks";
import { getZoneColor } from "../../services/zoneUtils";
import { querySubgraph } from "../../web3/subgraph";
import { ShipIcon } from "../shared/ShipIcon";
import { MissionOutcome } from "./MissionOutcome";

interface ClaimAnimationModalProps {
	sessionId: bigint;
	zone: number;
	shipTier?: ShipTier;
	/** Pass resolved data directly for history replay; omit to poll subgraph */
	mission?: ResolvedMission;
	onClose: () => void;
}

type ClaimPhase = "extracting" | "outcome";

const EXTRACTION_DURATION = 3500;

export function ClaimAnimationModal({
	sessionId,
	zone,
	shipTier,
	mission: provided,
	onClose,
}: ClaimAnimationModalProps) {
	const [phase, setPhase] = useState<ClaimPhase>("extracting");
	const [extractionDone, setExtractionDone] = useState(false);
	const zoneColor = getZoneColor(zone);

	// Poll subgraph for this session's resolved data (only when not provided)
	const { data: polled } = useResolvedSession(sessionId, !provided);

	const mission = provided ?? polled;

	// Timer for extraction animation
	useEffect(() => {
		const timer = setTimeout(() => setExtractionDone(true), EXTRACTION_DURATION);
		return () => clearTimeout(timer);
	}, []);

	// Transition to outcome when animation is done and data is ready
	useEffect(() => {
		if (extractionDone && mission) {
			setPhase("outcome");
		}
	}, [extractionDone, mission]);

	function handleBackdropClick(e: React.MouseEvent) {
		if (e.target === e.currentTarget) {
			onClose();
		}
	}

	return (
		<AnimatePresence>
			<motion.div
				className="claim-anim-modal__backdrop"
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				exit={{ opacity: 0 }}
				transition={{ duration: 0.2 }}
				onClick={handleBackdropClick}
			>
				<motion.div
					className="claim-anim-modal glass-panel-elevated"
					initial={{ opacity: 0, scale: 0.95, y: 20 }}
					animate={{ opacity: 1, scale: 1, y: 0 }}
					exit={{ opacity: 0, scale: 0.95, y: 20 }}
					transition={{ type: "spring", damping: 25, stiffness: 300 }}
				>
					{phase === "extracting" && (
						<ExtractionAnimation
							zoneColor={zoneColor}
							shipTier={shipTier ?? mission?.shipTier ?? ShipTier.Probe}
						/>
					)}

					{phase === "outcome" && mission && (
						<MissionOutcome
							mission={mission}
							planetName="Resource Site"
							planetColor={zoneColor}
							onClose={onClose}
						/>
					)}
				</motion.div>
			</motion.div>
		</AnimatePresence>
	);
}

// ---------------------------------------------------------------------------
// Subgraph query for a single resolved session by ID
// ---------------------------------------------------------------------------

interface SubgraphSession {
	id: string;
	shipTokenId: string;
	ship: { tier: "Probe" | "Frigate" | "Dreadnought" };
	outcome: string;
	pointsEarned: string | null;
	shipDestroyed: boolean;
	startedAt: string;
	resolvedAt: string | null;
	resolvedTimestamp: string | null;
}

const tierMap: Record<string, ShipTier> = {
	Probe: ShipTier.Probe,
	Frigate: ShipTier.Frigate,
	Dreadnought: ShipTier.Dreadnought,
};

const outcomeMap: Record<string, FarmOutcome> = {
	Destroyed: FarmOutcome.Destroyed,
	Poor: FarmOutcome.Poor,
	Standard: FarmOutcome.Standard,
	Rich: FarmOutcome.Rich,
	Jackpot: FarmOutcome.Jackpot,
};

function useResolvedSession(sessionId: bigint, enabled: boolean) {
	return useQuery({
		queryKey: ["resolvedSession", sessionId.toString()],
		queryFn: async (): Promise<ResolvedMission | null> => {
			const result = await querySubgraph<{ farmSession: SubgraphSession | null }>(
				`query ResolvedById($id: ID!) {
					farmSession(id: $id) {
						id
						shipTokenId
						ship { tier }
						outcome
						pointsEarned
						shipDestroyed
						startedAt
						resolvedAt
						resolvedTimestamp
					}
				}`,
				{ id: sessionId.toString() },
			);

			const s = result.farmSession;
			if (!s || !s.outcome) return null;

			return {
				sessionId: BigInt(s.id),
				shipTokenId: BigInt(s.shipTokenId),
				shipTier: tierMap[s.ship.tier] ?? ShipTier.Probe,
				outcome: outcomeMap[s.outcome] ?? FarmOutcome.Destroyed,
				pointsEarned: BigInt(s.pointsEarned ?? "0"),
				shipDestroyed: s.shipDestroyed,
				startedAtBlock: BigInt(s.startedAt),
				resolvedAtBlock: BigInt(s.resolvedAt ?? "0"),
				resolvedTimestamp: BigInt(s.resolvedTimestamp ?? "0"),
			};
		},
		enabled,
		refetchInterval: enabled ? 2_000 : false,
		staleTime: 0,
	});
}

// ---------------------------------------------------------------------------
// Extraction animation scene
// ---------------------------------------------------------------------------

function ExtractionAnimation({ zoneColor, shipTier }: { zoneColor: string; shipTier: ShipTier }) {
	const [statusIndex, setStatusIndex] = useState(0);

	useEffect(() => {
		const t1 = setTimeout(() => setStatusIndex(1), 1200);
		const t2 = setTimeout(() => setStatusIndex(2), 2500);
		return () => {
			clearTimeout(t1);
			clearTimeout(t2);
		};
	}, []);

	const statusText =
		statusIndex === 2
			? "Collecting resources..."
			: statusIndex === 1
				? "Extracting resources..."
				: "Scanning deposit...";

	return (
		<div className="claim-anim">
			{/* Starfield layers */}
			<div className="mission-anim__starfield mission-anim__starfield--far" />
			<div className="mission-anim__starfield mission-anim__starfield--mid" />
			<div className="mission-anim__starfield mission-anim__starfield--near" />

			{/* Resource planet / asteroid */}
			<div className="claim-anim__planet-wrapper">
				<div
					className="claim-anim__planet"
					style={{
						background: `radial-gradient(circle at 35% 35%, ${zoneColor}cc, ${zoneColor}44)`,
						boxShadow: `0 0 40px ${zoneColor}66, 0 0 80px ${zoneColor}22`,
					}}
				/>
				<div className="claim-anim__planet-ring" style={{ borderColor: `${zoneColor}33` }} />
			</div>

			{/* Ship positioned near planet */}
			<div className="claim-anim__ship">
				<ShipIcon tier={shipTier} size="lg" />
			</div>

			{/* Tractor beam from ship to planet */}
			{statusIndex >= 1 && (
				<div className="claim-anim__beam" style={{ background: `${zoneColor}15` }} />
			)}

			{/* Resource particles flowing from planet toward ship */}
			{statusIndex >= 1 && (
				<div className="claim-anim__particles">
					{[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
						<div
							key={`extract-p${n}`}
							className="claim-anim__particle"
							style={{
								background: zoneColor,
								animationDelay: `${n * 0.3}s`,
								top: `${42 + n * 1.6}%`,
							}}
						/>
					))}
				</div>
			)}

			{/* Status footer */}
			<div className="mission-anim__footer">
				<span className="mission-anim__status">{statusText}</span>
			</div>
		</div>
	);
}
